/** Componentes reutilizables: insignia de estado y selects de catálogo. */

import { useEffect, useState, type ReactNode } from 'react';
import { claseBadge } from '../util';
import { listarUsuarios, listarOficinas, type Funcionario } from '../servicios/consultas';
import { useAuth } from '../auth/AuthContext';
import type { Oficina } from '@cji/shared';

export function Badge({ estado }: { estado: string }) {
  return <span className={claseBadge(estado)}>{estado}</span>;
}

/** Encabezado verde con degradado (idéntico al módulo actual). */
export function Encabezado({ icono, titulo, children }: { icono: string; titulo: string; children?: ReactNode }) {
  const { usuario } = useAuth();
  const icon = icono.startsWith('bx') ? <i className={`bx ${icono}`} aria-hidden /> : <span aria-hidden>{icono}</span>;
  return (
    <div className="encabezado">
      <h1>{icon} {titulo}</h1>
      <p className="sub">
        Usuario: <strong>{alias(usuario?.email) || '—'}</strong> · CJIDF — Sistema de Gestión Documental
      </p>
      {children}
    </div>
  );
}

/** Alias del correo (lo de antes del @). */
export const alias = (email: string | null | undefined) =>
  email ? email.split('@')[0] : '';

/** Selector de usuarios BUSCABLE: se escribe para filtrar; muestra el alias. */
export function SelectUsuarios({
  valor,
  onChange,
  excluir,
  placeholder = 'Escriba para buscar…',
}: {
  valor: string;
  onChange: (email: string) => void;
  excluir?: string;
  placeholder?: string;
}) {
  const [usuarios, setUsuarios] = useState<Funcionario[]>([]);
  const [q, setQ] = useState('');
  const [abierto, setAbierto] = useState(false);
  useEffect(() => {
    void listarUsuarios().then(setUsuarios);
  }, []);

  const sel = usuarios.find((u) => u.email === valor);
  const f = q.trim().toLowerCase();
  const lista = usuarios
    .filter((u) => u.email !== excluir)
    .filter(
      (u) =>
        !f ||
        alias(u.email).toLowerCase().includes(f) ||
        u.email.toLowerCase().includes(f) ||
        `${u.nombre} ${u.apellido}`.toLowerCase().includes(f),
    )
    .slice(0, 60);

  return (
    <div className="combo">
      <input
        value={abierto ? q : sel ? alias(sel.email) : ''}
        placeholder={placeholder}
        onFocus={() => { setAbierto(true); setQ(''); }}
        onChange={(e) => { setQ(e.target.value); setAbierto(true); }}
        onBlur={() => setTimeout(() => setAbierto(false), 150)}
        autoComplete="off"
      />
      {abierto && (
        <div className="combo-dd">
          {lista.length === 0 && <div className="combo-vacio">Sin coincidencias</div>}
          {lista.map((u) => (
            <div
              key={u.email}
              className="combo-item"
              onMouseDown={() => { onChange(u.email); setAbierto(false); setQ(''); }}
            >
              <strong>{alias(u.email)}</strong>
              {`${u.nombre} ${u.apellido}`.trim() && (
                <span className="combo-sec"> · {`${u.nombre} ${u.apellido}`.trim()}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SelectOficinas({
  valor,
  onChange,
}: {
  valor: string;
  onChange: (id: string) => void;
}) {
  const [oficinas, setOficinas] = useState<Oficina[]>([]);
  useEffect(() => {
    void listarOficinas().then(setOficinas);
  }, []);
  return (
    <select value={valor} onChange={(e) => onChange(e.target.value)}>
      <option value="">Seleccione una oficina…</option>
      {oficinas.map((o) => (
        <option key={o.id} value={o.id}>
          {o.nombre}
        </option>
      ))}
    </select>
  );
}

/** Muestra el ALIAS del correo (lo de antes del @) para identificar usuarios. */
export function useNombresUsuarios() {
  return (email: string | null | undefined) => (email ? alias(email) : '—');
}
